import { KnowledgeArticle, createArticle } from '../types'

export const scannersArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'scanner-overview',
    title: 'Vulnerability Scanning Overview',
    section: 'scanners',
    summary: 'The vulnerability scanner uses Nuclei to detect security issues across web apps, APIs, and infrastructure. Findings are linked to compliance controls.',
    content: `
# Vulnerability Scanning Overview

The platform includes a built-in vulnerability scanner powered by Nuclei, an industry-standard security scanning tool.

## What It Scans

- **Web Applications** - Common vulnerabilities (XSS, SQLi, etc.)
- **APIs** - Authentication, authorization, data exposure
- **Infrastructure** - SSL/TLS issues, misconfigurations
- **Cloud Resources** - Exposed services, weak configurations

## Scan Modes

- **Quick Scan** - Fast scan of critical vulnerabilities only
- **Full Scan** - Comprehensive scan of all vulnerability types
- **Stealth Scan** - Slower, less detectable scanning

## How It Works

1. You provide target URLs, IPs, or hostnames
2. The scanner probes for open ports and services
3. Nuclei templates detect known vulnerabilities
4. Results are categorized by severity
5. Findings link to relevant compliance controls

## Severity Levels

- **Critical** - Immediate action required, exploitable vulnerabilities
- **High** - Serious issues that should be fixed soon
- **Medium** - Important issues to address in normal cycle
- **Low** - Minor issues or informational findings
- **Info** - Informational discoveries, not vulnerabilities
    `.trim(),
    tags: ['scanner', 'vulnerability', 'security', 'nuclei', 'penetration-testing', 'assessment'],
    relatedPages: ['/scanners'],
    icon: 'Radar',
    order: 1,
  }),

  createArticle({
    id: 'scanner-setup',
    title: 'Setting Up Scans',
    section: 'scanners',
    summary: 'Create scan configurations with targets (URLs, IPs), select scan mode, configure schedule, and run manual or automated scans.',
    content: `
# Setting Up Scans

Learn how to configure and run vulnerability scans.

## Creating a Scan Configuration

1. Go to **Scanners**
2. Click **New Scan Configuration**
3. Enter a name for your configuration
4. Add targets:
   - URLs (https://example.com)
   - IP addresses (192.168.1.1)
   - Hostnames (server.example.com)
   - CIDR ranges (192.168.1.0/24)

## Configuration Options

### Scan Mode
- **Quick** - 5-10 minutes, critical vulns only
- **Full** - 30-60 minutes, comprehensive
- **Stealth** - 60+ minutes, low detection

### Target Types
- **External** - Internet-facing assets
- **Internal** - Internal network assets

### Exclusions
Add paths or patterns to exclude from scanning:
- /logout
- /admin/* (if you don't want admin scanned)

## Running a Scan

### Manual Scan
1. Select your configuration
2. Click **Run Scan**
3. Monitor progress in real-time
4. Review results when complete

### Scheduled Scans
1. Enable "Scheduled Scanning" on the configuration
2. Select frequency (daily, weekly, monthly)
3. Scans run automatically at the scheduled time

## Best Practices

- Start with Quick scans to verify targets work
- Run Full scans during maintenance windows
- Review and triage results promptly
- Re-scan after remediation to verify fixes
    `.trim(),
    tags: ['scanner', 'configuration', 'targets', 'setup', 'scheduled-scans'],
    relatedPages: ['/scanners'],
    icon: 'Settings',
    order: 2,
  }),

  createArticle({
    id: 'scanner-results',
    title: 'Understanding Scan Results',
    section: 'scanners',
    summary: 'Scan results show vulnerabilities by severity with CVE references, affected URLs, remediation guidance, and links to compliance controls.',
    content: `
# Understanding Scan Results

Learn how to interpret and act on vulnerability scan results.

## Results Overview

After a scan completes, you'll see:
- **Summary** - Total findings by severity
- **Findings List** - All detected vulnerabilities
- **Affected Assets** - Which targets have issues

## Finding Details

Each finding includes:
- **Title** - Name of the vulnerability
- **Severity** - Critical, High, Medium, Low, Info
- **CVE** - Common Vulnerabilities and Exposures ID (if applicable)
- **Affected URL** - Where the vulnerability was found
- **Description** - What the vulnerability is
- **Remediation** - How to fix it
- **References** - Links to more information

## Taking Action

### Remediate
Fix the underlying issue in your application or infrastructure.

### Accept Risk
If you can't fix it, document why and accept the risk formally.

### False Positive
Mark findings that don't actually apply to your environment.

### Link to Controls
Connect findings to compliance controls for audit trails.

## Comparing Scans

Compare results between scans to track:
- New vulnerabilities introduced
- Vulnerabilities that were fixed
- Recurring issues that need attention

## Exporting Results

Export scan results for:
- Executive reporting
- Compliance audits
- Development team handoff
    `.trim(),
    tags: ['scanner', 'results', 'vulnerabilities', 'remediation', 'cve', 'findings'],
    relatedPages: ['/scanners'],
    icon: 'FileSearch',
    order: 3,
  }),
]
